/**
 * Settings Page
 * Shared between student and teacher roles
 * Sections: Edit Profile, Appearance, Change Password, Delete Account
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/layout/Layout';
import ThemeSelector from '../../components/ThemeSelector';
import {
  changePassword,
  updateProfile,
  deleteAccount,
} from '../../api/auth';

type Section = 'profile' | 'appearance' | 'password' | 'danger';

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();

  const [activeSection, setActiveSection] = useState<Section>('profile');

  // Profile form
  const [profileForm, setProfileForm] = useState({ full_name: '', email: '' });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMessage, setProfileMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password form
  const [passwordForm, setPasswordForm] = useState({ current: '', new_password: '', confirm: '' });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Delete account
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Initialize profile form from user data
  useEffect(() => {
    if (user) {
      setProfileForm({ full_name: user.full_name || '', email: user.email || '' });
    }
  }, [user]);

  // Handlers
  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileLoading(true);
    setProfileMessage(null);
    try {
      await updateProfile({
        full_name: profileForm.full_name,
        email: profileForm.email,
      });
      if (refreshUser) await refreshUser();
      setProfileMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (err: any) {
      setProfileMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update profile' });
    } finally {
      setProfileLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match' });
      return;
    }
    setPasswordLoading(true);
    setPasswordMessage(null);
    try {
      await changePassword({
        current_password: passwordForm.current,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current: '', new_password: '', confirm: '' });
      setPasswordMessage({ type: 'success', text: 'Password changed successfully' });
    } catch (err: any) {
      setPasswordMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to change password' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (deleteConfirmText !== 'DELETE') {
      setDeleteMessage({ type: 'error', text: 'Please type DELETE to confirm' });
      return;
    }
    setDeleteLoading(true);
    setDeleteMessage(null);
    try {
      await deleteAccount(deletePassword);
      await logout();
      navigate('/login');
    } catch (err: any) {
      setDeleteMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to delete account' });
      setDeleteLoading(false);
    }
  };

  const sections: { id: Section; label: string; danger?: boolean; icon: React.ReactNode }[] = [
    {
      id: 'profile',
      label: 'Edit Profile',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
    {
      id: 'appearance',
      label: 'Appearance',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      ),
    },
    {
      id: 'password',
      label: 'Change Password',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      ),
    },
    {
      id: 'danger',
      label: 'Delete Account',
      danger: true,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
          <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
    },
  ];

  /** Quiet section heading with a small tinted icon tile. */
  const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle: string; danger?: boolean }> = ({
    icon,
    title,
    subtitle,
    danger,
  }) => (
    <div className="mb-6 flex items-start gap-4">
      <span
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${
          danger
            ? 'bg-red-50 text-red-600 dark:bg-red-900/25 dark:text-red-400'
            : 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
        }`}
      >
        {icon}
      </span>
      <div>
        <h2 className={`text-lg font-extrabold leading-tight tracking-tight ${danger ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`}>
          {title}
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
      </div>
    </div>
  );

  const MessageBanner: React.FC<{ message: { type: 'success' | 'error'; text: string } | null }> = ({ message }) => {
    if (!message) return null;
    return (
      <div
        className={`px-4 py-3 rounded-2xl text-sm font-semibold mb-5 border-l-4 ${
          message.type === 'success'
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-500'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-500'
        }`}
      >
        {message.text}
      </div>
    );
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-8">
        {/* === Header === */}
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-3xl">Settings</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Manage your account and preferences.
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <nav className="w-full md:w-56 flex-shrink-0">
            <div className="rounded-2xl border border-gray-200 bg-white p-2 space-y-1 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
              {sections.map((s) => {
                const isActive = activeSection === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`w-full flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                      isActive
                        ? s.danger
                          ? 'bg-red-50 text-red-700 dark:bg-red-900/25 dark:text-red-400'
                          : 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-secondary-700 dark:hover:text-white'
                    }`}
                  >
                    <span
                      className={
                        isActive
                          ? s.danger
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-primary-700 dark:text-primary-300'
                          : 'text-gray-400 dark:text-gray-500'
                      }
                    >
                      {s.icon}
                    </span>
                    {s.label}
                  </button>
                );
              })}
            </div>
          </nav>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-soft sm:p-8 dark:border-secondary-700 dark:bg-secondary-800">
              {/* Edit Profile */}
              {activeSection === 'profile' && (
                <div>
                  <SectionHeader
                    icon={sections[0].icon}
                    title="Edit Profile"
                    subtitle="Update your personal information"
                  />
                  <MessageBanner message={profileMessage} />
                  <form onSubmit={handleUpdateProfile} className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">Username</label>
                      <input
                        type="text"
                        value={user?.username || ''}
                        disabled
                        className="w-full px-4 py-2.5 border-2 border-gray-100 dark:border-secondary-600 rounded-2xl bg-gray-50 dark:bg-secondary-700 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">Username cannot be changed</p>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">Full Name</label>
                      <input
                        type="text"
                        value={profileForm.full_name}
                        onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white transition-colors focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">Email</label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white transition-colors focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none"
                        required
                      />
                    </div>
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={profileLoading}
                        className="rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:opacity-50 disabled:shadow-none"
                      >
                        {profileLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Appearance */}
              {activeSection === 'appearance' && (
                <div>
                  <SectionHeader
                    icon={sections[1].icon}
                    title="Appearance"
                    subtitle="Customize the look and feel of your workspace"
                  />
                  <ThemeSelector />
                </div>
              )}

              {/* Change Password */}
              {activeSection === 'password' && (
                <div>
                  <SectionHeader
                    icon={sections[2].icon}
                    title="Change Password"
                    subtitle="Update your password to keep your account secure"
                  />
                  <MessageBanner message={passwordMessage} />
                  <form onSubmit={handleChangePassword} className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">Current Password</label>
                      <input
                        type="password"
                        value={passwordForm.current}
                        onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white transition-colors focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">New Password</label>
                      <input
                        type="password"
                        value={passwordForm.new_password}
                        onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white transition-colors focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none"
                        minLength={6}
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">Confirm New Password</label>
                      <input
                        type="password"
                        value={passwordForm.confirm}
                        onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white transition-colors focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 focus:outline-none"
                        minLength={6}
                        required
                      />
                    </div>
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={passwordLoading}
                        className="rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:opacity-50 disabled:shadow-none"
                      >
                        {passwordLoading ? 'Changing...' : 'Change Password'}
                      </button>
                    </div>
                  </form>
                </div>
              )}

              {/* Delete Account */}
              {activeSection === 'danger' && (
                <div>
                  <SectionHeader
                    icon={sections[3].icon}
                    title="Delete Account"
                    subtitle="Permanently delete your account and all associated data. This action cannot be undone."
                    danger
                  />
                  <MessageBanner message={deleteMessage} />
                  <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-2xl p-5 mb-6">
                    <div className="flex items-start">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30 mr-3">
                        <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                      </span>
                      <div>
                        <p className="text-sm font-bold text-red-800 dark:text-red-300">Warning</p>
                        <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                          This will permanently remove your profile, enrolled courses, and all your data.
                          If you are a teacher, your courses will also be deleted.
                        </p>
                      </div>
                    </div>
                  </div>
                  <form onSubmit={handleDeleteAccount} className="space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">Enter your password</label>
                      <input
                        type="password"
                        value={deletePassword}
                        onChange={(e) => setDeletePassword(e.target.value)}
                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white transition-colors focus:ring-2 focus:ring-red-500/30 focus:border-red-500 focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                        Type <span className="rounded-full bg-red-100 dark:bg-red-900/30 px-2 py-0.5 font-bold text-red-600 dark:text-red-400">DELETE</span> to confirm
                      </label>
                      <input
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                        placeholder="DELETE"
                        className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 transition-colors focus:ring-2 focus:ring-red-500/30 focus:border-red-500 focus:outline-none"
                        required
                      />
                    </div>
                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={deleteLoading || deleteConfirmText !== 'DELETE'}
                        className="rounded-full bg-red-500 px-5 py-2.5 text-sm font-bold text-white shadow-[0_4px_0_0_rgba(0,0,0,0.15)] transition-all hover:bg-red-600 active:translate-y-0.5 active:shadow-none disabled:opacity-50 disabled:shadow-none"
                      >
                        {deleteLoading ? 'Deleting...' : 'Permanently Delete Account'}
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SettingsPage;
